import React, { useState } from 'react';
import { auth, db } from '../../firebase/config';
import { doc, updateDoc } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import { logger } from '../../utils/logger';
import { FaUserMd, FaHardHat } from 'react-icons/fa';

interface Step0SectorSelectionProps {
  onComplete: (step: string) => void;
}

const Step0SectorSelection: React.FC<Step0SectorSelectionProps> = ({ onComplete }) => {
  const { t } = useTranslation();
  const [sector, setSector] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      if (!sector) {
        setError(t('onboarding.sector.errorRequired'));
        setLoading(false);
        return;
      }
      if (!auth.currentUser) {
        setError(t('onboarding.sector.errorUnauthenticated'));
        setLoading(false);
        return;
      }
      await updateDoc(doc(db, 'users', auth.currentUser.uid), {
        sector,
        updatedAt: new Date().toISOString()
      });
      logger.info('Step0SectorSelection', 'Sector guardado exitosamente', { sector });
      onComplete('sector');
    } catch (error: any) {
      logger.error('Step0SectorSelection', 'Error al guardar sector', error);
      setError(error.message || t('onboarding.sector.errorUnknown'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-3xl mx-auto p-8 bg-white rounded-[20px_40px_20px_40px] shadow-2xl border border-white/40 animate-fadeIn font-['Montserrat']">
      <h2 className="text-3xl font-bold text-red-600 mb-4 text-center">{t('onboarding.sector.title')}</h2>
      <p className="text-gray-600 text-center mb-8 max-w-2xl mx-auto">
        {t('onboarding.sector.description')}
      </p>
      <form onSubmit={handleSubmit} className="flex flex-col gap-8 max-w-2xl mx-auto">
        <div className="flex flex-col gap-4">
          <label className="text-gray-700 font-medium mb-2">{t('onboarding.sector.selectLabel')}</label>
          <div className="flex flex-row gap-8 justify-center">
            <button
              type="button"
              className={`flex-1 p-8 rounded-2xl border-2 transition-all duration-300 text-xl font-semibold shadow-md cursor-pointer flex flex-col items-center 
                ${sector === 'health' ? 'bg-red-600 text-white border-red-600 scale-105 shadow-lg' : 'bg-white text-gray-700 border-gray-200 hover:border-red-400'}
              `}
              onClick={() => setSector('health')}
            >
              <FaUserMd className={`mb-4 text-5xl ${sector === 'health' ? 'text-white' : 'text-red-600'}`} />
              {t('onboarding.sector.health')}
            </button>
            <button
              type="button"
              className={`flex-1 p-8 rounded-2xl border-2 transition-all duration-300 text-xl font-semibold shadow-md cursor-pointer flex flex-col items-center 
                ${sector === 'construction' ? 'bg-red-600 text-white border-red-600 scale-105 shadow-lg' : 'bg-white text-gray-700 border-gray-200 hover:border-red-400'}
              `}
              onClick={() => setSector('construction')}
            >
              <FaHardHat className={`mb-4 text-5xl ${sector === 'construction' ? 'text-white' : 'text-red-600'}`} />
              {t('onboarding.sector.construction')}
            </button>
          </div>
        </div>
        {error && <div className="text-red-600 text-center">{error}</div>}
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-red-600 text-white font-bold py-3 rounded-xl shadow-lg hover:bg-red-700 transition-all duration-300 disabled:opacity-50"
        >
          {loading ? t('common.saving') : t('onboarding.sector.continue')}
        </button>
      </form>
    </div>
  );
};

export default Step0SectorSelection; 